import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { api, diagnoseNoResponse } from '@/lib/api';
import toast from 'react-hot-toast';

/* Mirrors the backend rule exactly, so the form can never accept something
   the API will reject. */
const passwordRules = [
  { label: 'At least 8 characters', test: (v: string) => v.length >= 8 },
  { label: 'An uppercase letter',   test: (v: string) => /[A-Z]/.test(v) },
  { label: 'A lowercase letter',    test: (v: string) => /[a-z]/.test(v) },
  { label: 'A number',              test: (v: string) => /[0-9]/.test(v) },
  { label: 'A symbol',              test: (v: string) => /[^A-Za-z0-9]/.test(v) },
];

const schema = z.object({
  password: z.string().min(8),
  confirm: z.string().min(8),
}).refine((d) => d.password === d.confirm, { message: 'Passwords do not match', path: ['confirm'] });

type FormData = z.infer<typeof schema>;

type Status =
  | { state: 'checking' }
  | { state: 'valid'; email?: string }
  | { state: 'invalid'; reason: string }
  | { state: 'done' };

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const { register, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm<FormData>();
  const [status, setStatus] = useState<Status>({ state: 'checking' });

  const password = watch('password') ?? '';

  /* Check the link before showing the form - telling someone their link has
     expired after they have typed a new password twice is needless friction. */
  useEffect(() => {
    if (!token) {
      setStatus({ state: 'invalid', reason: 'This link is missing its reset token. Request a new one.' });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/auth/reset-password/validate', { params: { token } });
        if (cancelled) return;
        setStatus(data.valid
          ? { state: 'valid', email: data.email }
          : { state: 'invalid', reason: data.reason ?? 'This reset link is not valid.' });
      } catch (e: any) {
        if (cancelled) return;
        setStatus({
          state: 'invalid',
          reason: e?.response?.data?.message ?? 'We could not check this link. Please request a new one.',
        });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  async function onSubmit(values: FormData) {
    try {
      schema.parse(values);
      await api.post('/auth/reset-password', { token, password: values.password });
      setStatus({ state: 'done' });
      toast.success('Password updated.');
      setTimeout(() => navigate('/login'), 2200);
    } catch (e: any) {
      if (e?.name === 'ZodError') {
        return toast.error(e.issues?.[0]?.message ?? 'Check the password fields.');
      }
      if (!e?.response) return toast.error(await diagnoseNoResponse(), { duration: 9000 });
      toast.error(e.response.data?.message ?? 'Could not reset your password.', { duration: 9000 });
    }
  }

  return (
    <div className="relative min-h-[calc(100vh-4rem)] flex flex-col items-center justify-center px-4 py-12 overflow-hidden">
      <img
        src="https://images.unsplash.com/photo-1496747611176-843222e1e57c?w=1920&h=1280&fit=crop&auto=format&q=80"
        alt="" aria-hidden
        onError={(e) => { (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=1920&h=1280&fit=crop&auto=format&q=80'; }}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-br from-ink/90 via-ink/74 to-ink/92" />
      <div className="absolute inset-0 mashrabiya opacity-[0.06] pointer-events-none" />

      <div className="relative z-10 w-full max-w-[400px]">
        <div className="text-center mb-6">
          <Link to="/" className="display text-[44px] leading-none text-gold tracking-tight drop-shadow">Souqli</Link>
          <p className="text-cream/85 text-[15px] mt-3 leading-snug max-w-[32ch] mx-auto">
            Choose a new password for your account.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-frame border border-warmline p-5 sm:p-6">

          {status.state === 'checking' && (
            <div className="text-center py-8">
              <div className="w-8 h-8 rounded-full border-2 border-gold border-t-transparent animate-spin mx-auto mb-4" />
              <p className="text-[14px] text-mute">Checking your reset link…</p>
            </div>
          )}

          {status.state === 'invalid' && (
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-bad/10 grid place-items-center mx-auto mb-4 text-2xl">⚠️</div>
              <h1 className="display text-[22px] mb-2">Link no longer valid</h1>
              <p className="text-[14px] text-mute leading-relaxed mb-5">{status.reason}</p>
              <Link to="/forgot" className="btn btn-primary btn-lg w-full text-center mb-2">
                Request a new link
              </Link>
              <Link to="/login" className="btn btn-outline btn-sm w-full text-center">Back to log in</Link>
            </div>
          )}

          {status.state === 'done' && (
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-sage/15 grid place-items-center mx-auto mb-4 text-2xl">✓</div>
              <h1 className="display text-[22px] mb-2">Password updated</h1>
              <p className="text-[14px] text-mute leading-relaxed mb-5">
                For your security we signed out every other device. Taking you to the login page…
              </p>
              <Link to="/login" className="btn btn-primary btn-lg w-full text-center">Log in now</Link>
            </div>
          )}

          {status.state === 'valid' && (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
              {status.email && (
                <div className="rounded-luxe bg-gold/5 border border-gold/25 px-3 py-2 mb-1">
                  <span className="stamp text-[9px] text-gold-dark">RESETTING</span>
                  <div className="mono text-[13px] text-ink">{status.email}</div>
                </div>
              )}

              <div>
                <label className="stamp text-[10px] text-mute block mb-1.5">NEW PASSWORD</label>
                <input
                  className="input input-lg"
                  type="password"
                  autoComplete="new-password"
                  autoFocus
                  placeholder="New password"
                  {...register('password', { required: true })}
                />
              </div>

              <div>
                <label className="stamp text-[10px] text-mute block mb-1.5">CONFIRM PASSWORD</label>
                <input
                  className="input input-lg"
                  type="password"
                  autoComplete="new-password"
                  placeholder="Repeat new password"
                  {...register('confirm', { required: true })}
                />
                {errors.confirm && <p className="text-bad text-xs mt-1">{errors.confirm.message ?? 'Passwords must match'}</p>}
              </div>

              {/* Live checklist beats a rejection after submit. */}
              <ul className="space-y-1 pt-1">
                {passwordRules.map((r) => {
                  const met = r.test(password);
                  return (
                    <li key={r.label} className={`flex items-center gap-2 text-[12px] ${met ? 'text-sage-dark' : 'text-mute'}`}>
                      <span className={`w-4 h-4 rounded-full grid place-items-center text-[9px] shrink-0 ${met ? 'bg-sage/20' : 'bg-warmline'}`}>
                        {met ? '✓' : ''}
                      </span>
                      {r.label}
                    </li>
                  );
                })}
              </ul>

              <button disabled={isSubmitting} className="btn btn-lg btn-primary w-full !mt-4">
                {isSubmitting ? 'Updating…' : 'Update password'}
              </button>

              <p className="text-[12px] text-mute text-center leading-relaxed pt-1">
                Updating your password signs you out everywhere else.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
